// My Tournaments, player side — the chess player's own view of what they have
// entered, as opposed to the arbiter tab of the same name (which lists the
// tournaments an organizer RUNS, and lives in js/app.js).
//
// Everything here is derived from the player's own registrations
// (getMyRegistrations in js/services/firebase-auth.js). See the comment there
// for why registrations and not player lists are the source, and what that
// cannot show.
//
// The page is read-only on purpose. Withdrawing from a tournament stays where
// it already is — on the tournament's own page, next to the Register button
// that created the entry — so there is one place that does it rather than two.

import { escapeHtml } from '../utils/formatting.js';

// The three buckets, in the order they are shown. Now first: what a player
// opens this tab during a tournament to find is the tournament they are
// sitting in, and it should never be below a year of history.
const SECTIONS = [
    { key: 'ongoing',  title: 'Playing now',          blurb: 'Tournaments running today.',                        icon: 'ri-broadcast-line' },
    { key: 'upcoming', title: 'Registered for',       blurb: 'You have signed up; these have not started yet.',   icon: 'ri-calendar-check-line' },
    { key: 'finished', title: 'Previous tournaments', blurb: 'Tournaments you have played.',                      icon: 'ri-history-line' }
];

// What the entry's own status means to the PLAYER, which is not what it means
// to the arbiter. 'imported' is arbiter-speak for "pulled onto the player
// list"; from this side that is simply confirmation of a place.
function entryBadge(reg) {
    const status = String(reg.status || 'pending');
    if (status === 'imported') return { cls: 'active', label: 'Place confirmed' };
    if (status === 'rejected') return { cls: 'muted', label: 'Not accepted' };
    return { cls: 'muted', label: 'Awaiting confirmation' };
}

function formatLabel(format) {
    if (format === 'swiss') return 'Swiss';
    if (format === 'monrad') return 'Monrad';
    if (format === 'konrad') return 'Konrad';
    if (format === 'arena') return 'Arena';
    if (format === 'round-robin') return 'Round Robin';
    if (format === 'team') return 'Team';
    return 'Double Round';
}

function dateLabel(t) {
    if (!t || !t.startDate) return tr('No date');
    return t.endDate ? `${t.startDate} ${tr('to')} ${t.endDate}` : t.startDate;
}

// One card per registration, not per tournament: the same account legitimately
// holds several entries for one event (a parent signing up two children), and
// each is a separate place with its own status. The entered name is always
// shown for that reason — it is the only thing telling two such cards apart.
function cardHTML(reg) {
    const t = reg.tournament;
    const badge = entryBadge(reg);
    const who = [reg.givenName, reg.lastName].filter(Boolean).join(' ');
    const name = (t && t.name) || reg.tournamentName || tr('Unknown tournament');
    const group = reg.groupName ? ` • ${escapeHtml(reg.groupName)}` : '';

    // A card only opens a tournament that is still there to open. The click is
    // carried out by navigation.js, which watches for data-watch-tournament.
    const open = t
        ? ` data-watch-tournament="${escapeHtml(String(t.id))}" style="flex-direction: column; align-items: flex-start; gap: 0.5rem; cursor: pointer;"`
        : ` style="flex-direction: column; align-items: flex-start; gap: 0.5rem; opacity: 0.65;"`;

    return `
    <div class="stat-card tourney-card"${open}>
        <div style="display: flex; width: 100%; justify-content: space-between; align-items: center;">
            <div class="stat-icon blue" style="width: 40px; height: 40px; font-size: 1.25rem;"><i class="ri-trophy-line"></i></div>
            <span class="status-badge ${badge.cls}">${escapeHtml(tr(badge.label))}</span>
        </div>
        <div style="margin-top: 0.5rem;">
            <h3 style="color: var(--text-main); font-size: 1.1rem; margin-bottom: 0.25rem;">${escapeHtml(name)}</h3>
            <p class="user-role">${t ? escapeHtml(tr(formatLabel(t.format))) + ' • ' : ''}${escapeHtml(dateLabel(t))}${group}</p>
            ${t && t.location ? `<p class="user-role" style="color: var(--primary); font-size: 0.75rem;"><i class="ri-map-pin-line"></i> ${escapeHtml(t.location)}</p>` : ''}
        </div>
        <div style="margin-top: 0.5rem; font-size: 0.85rem; color: var(--text-muted);">
            ${who ? `<i class="ri-user-line"></i> ${escapeHtml(who)}` : ''}
        </div>
        ${t ? '' : `<div style="font-size: 0.8rem; color: var(--text-muted);">${escapeHtml(tr('This tournament is no longer public.'))}</div>`}
    </div>
    `;
}

function sectionHTML(section, regs) {
    if (regs.length === 0) return '';
    return `
    <section style="margin-bottom: 2rem;">
        <div style="display: flex; align-items: baseline; gap: 0.6rem; margin-bottom: 0.25rem;">
            <h3 style="color: var(--text-main); font-size: 1.05rem;">
                <i class="${section.icon}"></i> ${escapeHtml(tr(section.title))}
            </h3>
            <span class="user-role">${regs.length}</span>
        </div>
        <p class="page-subtitle" style="margin-bottom: 1rem;">${escapeHtml(tr(section.blurb))}</p>
        <div class="tournaments-grid">${regs.map(cardHTML).join('')}</div>
    </section>
    `;
}

function emptyHTML() {
    return `
    <div class="tournaments-empty">
        <img class="tournaments-empty-art" src="/assets/illustrations/no-tournaments.jpg"
            alt="Two people playing chess" loading="lazy">
        <h2 class="tournaments-empty-title">${escapeHtml(tr('No tournaments yet'))}</h2>
        <p class="tournaments-empty-desc">${escapeHtml(tr('When you sign up for a tournament, it shows up here — before, during and after it is played.'))}</p>
        <div class="tournaments-empty-actions">
            <button class="btn-primary btn-large" data-show-view="upcoming">
                <i class="ri-search-eye-line"></i> ${escapeHtml(tr('Find a tournament'))}
            </button>
            <button class="btn-outline btn-large" data-show-view="ongoing">
                <i class="ri-broadcast-line"></i> ${escapeHtml(tr('Browse ongoing tournaments'))}
            </button>
        </div>
    </div>
    `;
}

// Guards against a slow fetch from an earlier visit painting over a later one.
let renderToken = 0;

export async function renderMyPlayTournaments() {
    const body = document.getElementById('myplay-body');
    if (!body) return;

    const token = ++renderToken;
    if (!window.authApp || typeof window.authApp.getMyRegistrations !== 'function') return;

    let regs;
    try {
        regs = await window.authApp.getMyRegistrations();
    } catch (e) {
        console.error('My Tournaments (player) failed to load:', e);
        if (renderToken !== token) return;
        body.innerHTML = `<div class="empty-state-container"><div class="empty-state-content glass">
            <h3>${escapeHtml(tr('Could not load your tournaments'))}</h3>
            <p>${escapeHtml(tr('Check your connection and try again.'))}</p>
        </div></div>`;
        return;
    }
    if (renderToken !== token) return;

    if (regs.length === 0) {
        body.innerHTML = emptyHTML();
        return;
    }

    // A registration whose tournament could not be read is still the player's
    // entry, so it is kept — filed by the date it was for, or with the past
    // when there is nothing left to date it by.
    const bucketed = { ongoing: [], upcoming: [], finished: [] };
    regs.forEach(r => {
        const status = (r.tournament && r.tournament.status) || 'finished';
        (bucketed[status] || bucketed.finished).push(r);
    });

    body.innerHTML = SECTIONS.map(s => sectionHTML(s, bucketed[s.key])).join('');
}
